import React, { useMemo, useRef, useState } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { FormControl, TextField, Autocomplete, Checkbox } from '@mui/material';
import { Controller } from 'react-hook-form';

import { getFieldErrorMessage, useFormContext } from '../FormProvider';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Props {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  fullWidth?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  margin?: 'none' | 'dense' | 'normal';
}

export const ControlledAutocompleteField: React.FC<Props> = ({
  name,
  label,
  options,
  multiple = false,
  disabled = false,
  fullWidth = true,
  margin = 'normal',
}) => {
  const { control, errors } = useFormContext();

  const [search, setSearch] = useState('');
  const searchRef = useRef(search);
  searchRef.current = search;

  const sortedOptions = useMemo(() => [...options].sort((a, b) => a.label.localeCompare(b.label)), [options]);

  return (
    <FormControl fullWidth={fullWidth} margin={margin}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          const selectedValue = multiple
            ? sortedOptions.filter((option) => (value || []).includes(option.value))
            : sortedOptions.find((option) => option.value === value) || null;

          return (
            <Autocomplete
              multiple={multiple}
              disabled={disabled}
              options={sortedOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue?.value}
              inputValue={search}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  setSearch(newInputValue);
                } else if (reason === 'clear') {
                  setSearch('');
                }
              }}
              value={selectedValue}
              onChange={(event, newValue) => {
                if (Array.isArray(newValue)) {
                  onChange(newValue.map((item) => item.value));
                } else {
                  onChange(newValue?.value || '');
                }
                setSearch('');
              }}
              renderOption={({ key, ...optionProps }, option, { selected }) => (
                <li key={key} {...optionProps}>
                  <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  error={!!errors?.[name]}
                  helperText={getFieldErrorMessage(errors?.[name]) || ''}
                />
              )}
              noOptionsText="Ничего не найдено"
            />
          );
        }}
      />
    </FormControl>
  );
};
